import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { classNames } from 'primereact/utils';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCreateSite, useGetSites } from '../../../../../../hooks/api/sites';
import useNotificationContext from '../../../../../../hooks/notification/useNotificationContext';
import { COMPANY_WIDE_VALUE } from '../../../../../../shared/constants/sites.constant';
import { ToastSeverity } from '../../../../../../shared/enums/toast-severity';
import { ISite } from '../../../../../../shared/interfaces/ISite';
import ErrorMessage from '../../../../../error-message';
import styles from './EnclosedSitesMultiSelect.module.css';
import InlineAddSiteForm, { InlineAddSiteFormData } from './InlineAddSiteForm';

type EnclosedSitesMultiSelectProps = {
  disabled?: boolean;
};

const EnclosedSitesMultiSelect = ({
  disabled = false,
}: EnclosedSitesMultiSelectProps): JSX.Element => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const { notify } = useNotificationContext();
  const queryClient = useQueryClient();

  const { data: sites = [], isLoading: isSitesLoading } = useGetSites();

  const multiSelectRef = useRef<MultiSelect | null>(null);
  const [isAddingInline, setIsAddingInline] = useState(false);

  const name = 'enclosedSites';

  const {
    field: { value, ref, onChange },
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
    defaultValue: [],
  });

  const options = useMemo(() => {
    const siteOptions = sites.map((site) => {
      return {
        label: `${site.name} (${site.bpns})`,
        value: site.id,
      };
    });

    if (sites.length > 0) {
      return [
        { label: t('addCertificateDialog.inputs.companyWide'), value: COMPANY_WIDE_VALUE },
        ...siteOptions,
      ];
    }

    return siteOptions;
  }, [sites, t]);

  const handleOnChange = useCallback(
    (event: MultiSelectChangeEvent) => {
      const newValue = event.value as (number | string)[];

      const hasCompanyWide = newValue.includes(COMPANY_WIDE_VALUE);
      const prevHadCompanyWide = (value as (number | string)[]).includes(COMPANY_WIDE_VALUE);

      if (hasCompanyWide && !prevHadCompanyWide) {
        onChange([COMPANY_WIDE_VALUE]);
      } else if (hasCompanyWide && newValue.length > 1) {
        onChange(
          newValue.filter((v) => {
            return v !== COMPANY_WIDE_VALUE;
          }),
        );
      } else {
        onChange(newValue);
      }
    },
    [onChange, value],
  );

  const { mutate: createSite, isLoading: isCreating } = useCreateSite({
    onSuccess: (newSite) => {
      // Optimistically add new site to cache so options list updates immediately,
      // then select it. Background refetch will reconcile with server state.
      queryClient.setQueryData<ISite[]>(['sites'], (old = []) => {
        return [...old, newSite];
      });
      queryClient.invalidateQueries({ queryKey: ['sites'] });

      const currentValue = (value as (number | string)[]).filter((v) => {
        return v !== COMPANY_WIDE_VALUE;
      });
      onChange([...currentValue, newSite.id]);

      notify({
        severity: ToastSeverity.SUCCESS,
        detail: t('manageSitesDialog.toastMessages.createSuccess'),
        life: 5000,
      });

      setIsAddingInline(false);
    },
    onError: () => {
      notify({
        severity: ToastSeverity.ERROR,
        detail: t('manageSitesDialog.toastMessages.createFailure'),
      });
    },
  });

  const handleInlineSubmit = (data: InlineAddSiteFormData) => {
    createSite({ name: data.name.trim(), bpns: data.bpns.trim() });
  };

  const handleInlineCancel = () => {
    setIsAddingInline(false);
    // PrimeReact MultiSelect closes panel on state-change re-renders.
    // setTimeout defers show() to after React re-render, keeping panel open.
    setTimeout(() => {
      multiSelectRef.current?.show();
    });
  };

  const handleAddNewSiteClick = () => {
    setIsAddingInline(true);
    // PrimeReact MultiSelect closes panel on state-change re-renders.
    // setTimeout defers show() to after React re-render, keeping panel open.
    setTimeout(() => {
      multiSelectRef.current?.show();
    });
  };

  const panelFooterTemplate = () => {
    return (
      <div
        className='p-2'
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Divider className='my-2' />

        {isAddingInline ? (
          <InlineAddSiteForm
            existingSites={sites}
            isSubmitting={isCreating}
            onSubmit={handleInlineSubmit}
            onCancel={handleInlineCancel}
          />
        ) : (
          <Button
            label={t('addCertificateDialog.inputs.addNewSite')}
            text
            size='small'
            onClick={handleAddNewSiteClick}
            className='w-full'
            type='button'
          />
        )}
      </div>
    );
  };

  return (
    <div className={classNames('field p-fluid', styles['enclosed-sites-multiselect'])}>
      <span className='p-float-label'>
        <MultiSelect
          id={name}
          value={value}
          appendTo='self'
          panelClassName={styles['multiselect-panel-upward']}
          ref={(el) => {
            ref(el);
            multiSelectRef.current = el;
          }}
          onChange={handleOnChange}
          options={options}
          optionLabel='label'
          optionValue='value'
          showSelectAll={false}
          showClear={false}
          disabled={disabled}
          loading={isSitesLoading}
          emptyMessage={t('addCertificateDialog.inputs.enclosedSitesEmpty')}
          className={classNames({
            'p-invalid': error,
          })}
          pt={{
            header: {
              className: 'hidden',
            },
          }}
          panelFooterTemplate={panelFooterTemplate}
        />
        <label htmlFor={name}>{t('addCertificateDialog.inputs.enclosedSites')}</label>
      </span>

      {invalid && (
        <div className={classNames(styles['error-message-box'])}>
          <ErrorMessage message={error?.message || ''} />
        </div>
      )}
    </div>
  );
};

export default EnclosedSitesMultiSelect;
